import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { RemixBreadcrumbs } from '#app/components/navigation/remix-breadcrumbs.tsx'
import { Code } from '#app/components/typography/code.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { CodeBlock } from '#app/old/components/data-display/code-block.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Source, type componentProps, Styling, Features, Usage } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Breadcrumbs',
}

const component: componentProps = {
	name: 'Breadcrumbs',
	shortName: 'Breadcrumbs',
	fileName: 'breadcrumbs',
	category: 'navigation',
	description: ['A hierarchy of links to the current page in an application.'],
	features: [
		'Implemented as an ordered list of links.',
		'The last link is automatically marked as the current page using aria-current.',
		'Hover, press, and keyboard focus states are provided for easy styling.',
	],
	parts: [
		{
			name: 'Breadcrumbs',
			description: 'A hierarchy of links to the current page in an application.',
		},
	],
	usage: `function Example() {
	const items = [
		{id: 1, label: 'Home', href: '/'},
		{id: 2, label: 'Navigation', href: '/components/navigation'},
		{id: 3, label: 'Breadcrumbs', href: '/components/navigation/breadcrumbs'},
	]

	return <Breadcrumbs items={items} />
}`,
	styling: `/* ### Navigation ### */
@layer components {
	.breadcrumbs {
		@apply flex flex-wrap;

		& .breadcrumb {
			@apply flex items-center;

			& a {
				@apply p-1 text-foreground opacity-50 data-[disabled]:opacity-100;
			}

			& .icon {
				@apply h-4 w-4 opacity-50;
			}
		}
	}
}`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const BreadcrumbsRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, features, usage, shortName, fileName, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Features features={features} />
			<Usage usage={usage} shortName={shortName} />
			<RemixBreadcrumbs className="my-4" />
			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
			<H2>Notes</H2>
			<P>
				The <Code>{'<Breadcrumbs />'}</Code> component receives an array of items to render as links. But it's up to you to figure out how to generate the items array based on your
				framework.
			</P>
			<P>I suggest you create an additional component that encapsulates this logic.</P>
			<P>
				Here's how you'd do it in{' '}
				<a href="https://remix.run/docs/en/main/guides/breadcrumbs" target="_blank" rel="noreferrer">
					Remix
				</a>
				:
			</P>
			<CodeBlock
				code={`import { useMatches } from '@remix-run/react'
import { type ElementRef, forwardRef } from 'react'
import { type BreadcrumbsProps } from 'react-aria-components'
import { type BreadcrumbItem, Breadcrumbs } from './breadcrumbs.tsx'

const RemixBreadcrumbs = forwardRef<ElementRef<typeof Breadcrumbs>, BreadcrumbsProps<BreadcrumbItem>>(({ ...props }, ref) => {
	const matches = useMatches()
	const items: BreadcrumbItem[] = matches
		.filter(match => match.handle && (match.handle as { breadcrumb: BreadcrumbItem }).breadcrumb)
		.map(match => {
			const { id } = match
			const { href, label } = (match.handle as { breadcrumb: BreadcrumbItem }).breadcrumb
			return { id, href, label }
		})
	return <Breadcrumbs ref={ref} items={items} {...props} />
})
RemixBreadcrumbs.displayName = 'RemixBreadcrumbs'

export { RemixBreadcrumbs }`}
				filename="RemixBreadcrumbs"
				extension="tsx"
			/>
			<P>
				Then, just place this <Code>{'<RemixBreadcrumbs />'}</Code> component wherever you want breadcrumbs to show up in your app.
			</P>
			<P>
				Remember that, in Remix, each route decides if and how it will contribute to the breadcrumbs using a <Code>handle</Code> export.
			</P>
			<CodeBlock
				code={`export const handle = {
	breadcrumb: () => <Link to="/parent">Some Route</Link>,
}`}
				filename="RemixBreadcrumbs"
				extension="tsx"
			/>
		</>
	)
}
export default BreadcrumbsRoute
